import React, { useMemo, useState } from 'react';

import { Box, Divider, Pagination, Stack, Typography } from '@mui/material';
import { Image } from '@realadvisor/image';
import { format, parseISO } from 'date-fns';
import { Home } from 'lucide-react';

import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useEntityTransactions } from '@modules/_shared/components/visibility-drawer/hooks/useEntityData';
import type { NormalizedTransaction } from '@modules/_shared/components/visibility-drawer/queries';
import DrawerEmptyState from '@modules/_shared/components/visibility-drawer/sections/DrawerEmptyState';
import EditableSectionHeader, {
  PencilEditIcon,
} from '@modules/_shared/components/visibility-drawer/sections/EditableSectionHeader';
import { TransactionDetailsModal } from '@modules/_shared/components/visibility-drawer/sections/TransactionDetailsModal';
import { VerificationStatus } from '@modules/_shared/components/visibility-drawer/sections/VerificationStatus';
import type { SectionProps } from '@modules/_shared/components/visibility-drawer/types';
import { AddTransactionButton } from '@modules/visibility-team/AddTransactionButton';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

import '@modules/_shared/components/visibility-drawer/queries/agent-queries';

const SoldBadge = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      bgcolor: 'primary.main',
      color: '#fff',
      borderRadius: '4px',
      fontSize: '0.625rem',
      fontWeight: 600,
      px: 1,
      py: 0.125,
      position: 'absolute',
      top: 6,
      left: 6,
      textAlign: 'center',
      letterSpacing: '0.05em',
    }}
  >
    {children}
  </Box>
);

const TransactionCard = ({
  transaction,
  onClick,
}: {
  transaction: NormalizedTransaction;
  onClick: () => void;
}) => {
  const { t } = useLocale();
  const formattedDate = transaction.signatureDate
    ? format(parseISO(transaction.signatureDate), 'MMMM yyyy')
    : null;

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        width: '100%',
        borderRadius: '8px',
        transition: 'background-color 0.15s ease',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        outline: 'none',
        p: 0.5,
        '&:hover .edit-icon': {
          opacity: 1,
        },
      }}
    >
      {/* Image with badge */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Box
          sx={{
            width: { xs: 80, sm: 120 },
            height: { xs: 80, sm: 88 },
            overflow: 'hidden',
            borderRadius: '8px',
            bgcolor: 'grey.100',
          }}
        >
          {transaction.imageUrl ? (
            <Image
              src={transaction.imageUrl}
              options={{ w: 192, h: 112 }}
              css={{
                objectFit: 'cover',
                objectPosition: 'center',
                width: '100%',
                height: '100%',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Home size={32} color={customPalette.gray400} strokeWidth={1.5} />
            </Box>
          )}
        </Box>
        <SoldBadge>{t('SOLD')}</SoldBadge>
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: 1.5,
          flex: 1,
          minWidth: 0,
          gap: 0.25,
        }}
      >
        {/* Title row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary',
              flex: 1,
              minWidth: 0,
            }}
          >
            {transaction.propertyType}
            {transaction.numberOfRooms != null &&
              ` · ${transaction.numberOfRooms} ${t('Rooms')}`}
          </Typography>
          {formattedDate && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {formattedDate}
            </Typography>
          )}
        </Box>

        {/* Location */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {transaction.locality}
        </Typography>

        {/* Agent + verification */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 0.25,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre-line',
              }}
            >
              {t('Sold by {{agent}}\nwith {{team}}', {
                agent: transaction.soldByAgent ?? '',
                team: transaction.soldByTeam ?? '',
              })}
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <VerificationStatus isVerified={transaction.isVerified} />
          </Box>
        </Box>

        {/* Mobile date and verification */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, mt: 0.25 }}>
          {formattedDate && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {formattedDate}
            </Typography>
          )}
          <VerificationStatus isVerified={transaction.isVerified} />
        </Box>
      </Box>

      <PencilEditIcon sx={{ alignItems: 'flex-start', pt: 0.5, px: 0.5 }} />
    </Box>
  );
};

const EmptyState = () => {
  const { t } = useLocale();
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <DrawerEmptyState
        icon={Home}
        message={t('No transactions found in the last 2 years')}
      />
      <AddTransactionButton />
    </Stack>
  );
};

const TransactionsSection = ({ entity, teamId }: SectionProps) => {
  const { t } = useLocale();
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const { data, totalCount, loading, error, setErrorLike } =
    useEntityTransactions(entity, {
      teamId,
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
    });

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const hasTransactions = data.length > 0;
  const isInitialLoading = loading && !totalCount;

  const transactionsContent = useMemo(() => {
    if (isInitialLoading) {
      return <LoadingSpinner />;
    }
    if (!hasTransactions && !loading) {
      return <EmptyState />;
    }
    return (
      <Stack
        direction="column"
        sx={{
          width: '100%',
        }}
      >
        <Stack direction="column">
          {loading ? (
            <Stack
              sx={{
                alignItems: 'center',
                py: 4,
              }}
            >
              <LoadingSpinner />
            </Stack>
          ) : (
            data.map((transaction, index) => (
              <Box key={transaction.id}>
                <TransactionCard
                  transaction={transaction}
                  onClick={() => setSelectedTransactionId(transaction.id)}
                />
                {index < data.length - 1 && <Divider sx={{ my: 1 }} />}
              </Box>
            ))
          )}
        </Stack>
        {totalPages > 1 && (
          <Stack
            sx={{
              alignItems: 'center',
              mt: 3,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        )}
        <AddTransactionButton sx={{ mt: 3 }} />
      </Stack>
    );
  }, [
    currentPage,
    data,
    hasTransactions,
    isInitialLoading,
    loading,
    totalPages,
  ]);

  if (error && !data.length) {
    console.error('Error loading transactions:', error);
    return (
      <EditableSectionHeader title={t('Transactions')}>
        <Typography sx={{ color: 'error.main' }}>
          {t('Error loading transactions')}
        </Typography>
      </EditableSectionHeader>
    );
  }

  const titleWithCount =
    totalCount > 0 ? `${t('Transactions')} (${totalCount})` : t('Transactions');

  return (
    <>
      <EditableSectionHeader title={titleWithCount}>
        {transactionsContent}
      </EditableSectionHeader>

      {selectedTransactionId && (
        <TransactionDetailsModal
          transactionId={selectedTransactionId}
          onClose={() => setSelectedTransactionId(null)}
        />
      )}

      <MutationErrorSnackbar error={error} onClose={() => setErrorLike(null)} />
    </>
  );
};

export default TransactionsSection;
